import { SmsMessageStatus } from 'src/graphql.generated'
import { ActivityContext, ChatLink, ChatMessageLink } from '../../api/patientConversation'
import { PatientHistoryEntry } from '../../api/useAdvancedPatientHistory'
import { Failure } from '../../api/failureReason'

export type ForwardedFeedItem =
  | { kind: 'chat'; label: string; fromPatient?: boolean }
  | { kind: 'activity'; label: string; detail: string | null; context: ActivityContext | null }
  | { kind: 'history'; label: string; entry: PatientHistoryEntry }

export const forwardedItemPreviewLabel = (item: ForwardedFeedItem): string => {
  if (item.kind === 'chat') {
    return item.fromPatient === false ? 'Care team message' : 'Patient message'
  }
  return item.label
}

// PHI: `downloadURL` is a short-lived signed URL, held only in memory.
export type MessageAttachment = {
  fileID: string
  filename: string
  contentType: string
  downloadURL: string | null
}

export const attachmentKind = (attachment: MessageAttachment): 'image' | 'file' =>
  attachment.contentType.startsWith('image/') ? 'image' : 'file'

export type CareTeamChatMessage = {
  id: string
  authorCoachId: string
  text: string
  forwardedItem: ForwardedFeedItem | null
  attachments: MessageAttachment[]
  createdAt: string
}

export type CareTeamThreadSummary = {
  threadId: string
  participantAUid: string
  participantBUid: string
  // Absent on a thread the viewer just started, until the service lists it.
  unreadCount?: number
}

export type CareTeamRosterEntry = { id: string; name: string; role: string; categoryId: string }

export type EditingMessage = { messageID: string; text: string; createdAt: Date }

// A lesson response or questionnaire picked for "Comment in chat", pending until sent.
export type PendingComment = ChatLink & ChatMessageLink

export type ChatMessageBubbleEntry = {
  variant?: 'message'
  id: string
  text: string
  createdAt: string
  isMine: boolean
  authorCoachId: string | null
  // Replaces `CoachName`, which appends the profile's title; blank holds the place while names load.
  authorName?: string
  authorRole: string | null
  authorCategory?: string | null
  channel?: 'inApp' | 'sms'
  smsStatus?: SmsMessageStatus | null
  forwardedItem?: ForwardedFeedItem | null
  linkedTo?: ChatMessageLink | null
  attachments?: MessageAttachment[]
  onEdit?: () => void
}

type ChatActivityBubbleEntry = { variant: 'activity'; id: string; text: string; createdAt: string }

export type ChatBubbleEntry = ChatMessageBubbleEntry | ChatActivityBubbleEntry

export type SendMessageError = Error & { rejectedFileIDs?: string[]; failure?: Failure }
